import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { Result } from '@/classes/result';
import type { Employee } from '@/types/employee';

import { Feature } from '@/enums/feature';
import { OrganizationRole } from '@/enums/roles';
import { ErrorMessage } from '@/messages/error';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import type { EmployeeForm, OrganizationEmployee } from '@/types/employee';
import type { AccessibleOrganization } from '@/types/organization';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';
import toast from '@/utils/toast';

export const useEmployeeStore = defineStore('employees', () => {
    const employeesInOrganizations = ref<OrganizationEmployee[]>([]);
    const parentOrganizationEmployees = ref<OrganizationEmployee[]>([]);

    const filteredOrganizationEmployees = computed(() => {
        const uniqueEmployeeIds = new Set<string>();

        return parentOrganizationEmployees.value.filter((rootOrganizationEmployee: OrganizationEmployee) => {
            const employee = rootOrganizationEmployee.employee as Employee;
            const employeeId = employee._id;

            if (uniqueEmployeeIds.has(employeeId)) return false;

            uniqueEmployeeIds.add(employeeId);

            const isEmployeeNotInCurrentOrganization = !employeesInOrganizations.value?.some(
                (organizationEmployee) => (organizationEmployee.employee as Employee)._id === employeeId
            );
            const isAdminOrSuperAdmin =
                (employee.isSuperAdmin || rootOrganizationEmployee.roles.includes(OrganizationRole.Admin)) &&
                !rootOrganizationEmployee.features?.includes(Feature.ActionCoordinatorRole);

            return isEmployeeNotInCurrentOrganization && !isAdminOrSuperAdmin;
        });
    });

    async function fetch(): Promise<Employee[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `employees`
            });

            const employees = result.object as Employee[];

            return employees;
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEES_FETCH_FAILED);
        }
    }

    async function fetchById(id: string): Promise<Employee | null> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `employees/${id}`
            });

            const employee = result.object as Employee;

            return employee;
        } catch (error) {
            return handleAxiosError(error, [], 'Medewerker kon niet opgehaald worden');
        }
    }

    /**
     *
     * @param organizationIds
     * @returns Returns employees in organizations
     */
    async function fetchInOrganization(organizationId: string): Promise<OrganizationEmployee[]> {
        try {
            employeesInOrganizations.value = await fetchInOrganizationIds([organizationId]);

            return employeesInOrganizations.value;
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEES_FETCH_FAILED);
        }
    }

    /**
     *
     * @param organizationIds
     * @returns Returns employees in parent organizations
     */
    async function fetchInParentOrganizations(organizationIds: string[]): Promise<OrganizationEmployee[]> {
        try {
            parentOrganizationEmployees.value = await fetchInOrganizationIds(organizationIds);

            return parentOrganizationEmployees.value;
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEES_FETCH_FAILED);
        }
    }

    async function fetchInOrganizationIds(organizationIds: string[]): Promise<OrganizationEmployee[]> {
        try {
            if (organizationIds.length === 0) throw new Error('Geen organisatie ids meegegeven');
            const organizationIdsString = organizationIds.join(',');

            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationIdsString}/employees`
            });

            return result.object as OrganizationEmployee[];
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEES_FETCH_FAILED);
        }
    }

    async function create(employee: EmployeeForm, organizationId: string): Promise<Result> {
        try {
            const authStore = useAuthStore();

            if (!authStore.getLoggedInEmployee()) {
                return new Result(500, 'Je bent niet ingelogd', null);
            }
            employee.currentOrganization = organizationId;

            const result = await callApi({
                method: 'POST',
                endpoint: `employees/`,
                body: {
                    employee: employee,
                    createdBy: authStore.getLoggedInEmployee()._id
                }
            });

            toast.success('EmployeeCreated');

            return result;
        } catch (error) {
            return handleAxiosError(error, [400], ErrorMessage.EMPLOYEE_CREATION_FAILED);
        }
    }

    async function login(email: string, password?: string): Promise<Result> {
        try {
            const result = await callApi({
                method: 'POST',
                endpoint: password ? 'employees/login' : 'employees/login-as',
                body: {
                    email,
                    password
                },
                jwtTokenRequired: !password
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [400, 403]);
        }
    }

    async function setEmployeeInactive(id: string): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `employees/${id}/inactive`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Medewerker kon niet inactief gezet worden');
        }
    }
    async function setEmployeeActive(id: string): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `employees/${id}/active`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Medewerker kon niet actief gezet worden');
        }
    }

    async function setCurrentOrganization(organizationId: string): Promise<Result> {
        try {
            const authStore = useAuthStore();
            const result = await callApi({
                method: 'PUT',
                endpoint: `employees/${authStore.getLoggedInEmployee()._id}/organization/set-current/${organizationId}`
            });

            const organizationStore = useOrganizationStore();
            const organization = await organizationStore.fetchById(organizationId);

            if (!organization) throw new Error(ErrorMessage.ORGANIZATION_NOT_FOUND);
            if (!organization) throw new Error(ErrorMessage.ORGANIZATION_NOT_FOUND);

            authStore.currentOrganization = organization as AccessibleOrganization;

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Kon niet wisselen van bedrijf';
            return handleAxiosError(error, [], errorMessage);
        }
    }

    async function updateEmployee(employee: EmployeeForm): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `employees/${employee._id}`,
                body: employee
            });

            toast.success('EmployeeUpdated');

            return result;
        } catch (error) {
            return handleAxiosError(error, [400], ErrorMessage.EMPLOYEE_UPDATE_FAILED);
        }
    }

    async function getAccessibleOrganizations(employeeId: string): Promise<AccessibleOrganization[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/accessible/${employeeId}/`
            });

            return result.object as AccessibleOrganization[];
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijf van de medewerker kon niet opgehaald worden');
        }
    }

    async function forgotPassword(email: string): Promise<void> {
        try {
            await callApi({
                method: 'POST',
                endpoint: 'employees/forgot-password',
                body: { email },
                jwtTokenRequired: false
            });
        } catch (error) {
            return handleAxiosError(error, [], 'Wachtwoord vergeten link kon niet gestuurd worden');
        }
    }

    async function resetPassword(token: string, password: string, confirmPassword: string): Promise<void> {
        try {
            await callApi({
                method: 'POST',
                endpoint: `employees/reset-password/${token}`,
                body: { password, confirmPassword },
                jwtTokenRequired: false
            });

            toast.success('PasswordResetLinkSent');
        } catch (error) {
            return handleAxiosError(error, [], 'Wachtwoord vergeten link kon niet gestuurd worden');
        }
    }

    async function changePassword(employeeId: string, currentPassword: string, password: string, confirmPassword: string): Promise<Result> {
        try {
            return await callApi({
                method: 'PUT',
                endpoint: `employees/change-password/${employeeId}`,
                body: { password, currentPassword, confirmPassword }
            });
        } catch (error) {
            return handleAxiosError(error, [], 'Wachtwoord kon niet veranderd worden');
        }
    }

    async function changeEmail(employeeId: string, currentEmail: string, newEmail: string, password: string): Promise<Result> {
        try {
            return await callApi({
                method: 'POST',
                endpoint: `employees/change-email/${employeeId}`,
                body: { currentEmail, newEmail, password }
            });
        } catch (error) {
            return handleAxiosError(error, [400], 'E-mailadres wijziging kon niet aangevraagd worden');
        }
    }

    async function verifyChangeEmail(token: string): Promise<void> {
        try {
            await callApi({
                method: 'POST',
                endpoint: `employees/change-email/verify/${token}`
            });

            const authStore = useAuthStore();
            const employeeStore = useEmployeeStore();
            const employee = await employeeStore.fetchById(authStore.getLoggedInEmployee()._id);

            if (employee) {
                authStore.loggedInEmployee = employee;
            } else {
                toast.warning('EmailUpdatedButNeedsRefresh');
                authStore.logout();
            }
        } catch (error) {
            return handleAxiosError(error, [], 'E-mailadres kon niet gewijzigd worden');
        }
    }

    return {
        getAccessibleOrganizations,
        fetch,
        fetchById,
        login,
        setEmployeeInactive,
        setEmployeeActive,
        setCurrentOrganization,
        create,
        updateEmployee,
        forgotPassword,
        resetPassword,
        changePassword,
        changeEmail,
        verifyChangeEmail,
        fetchInOrganization,
        employeesInOrganizations,
        fetchInParentOrganizations,
        parentOrganizationEmployees,
        filteredOrganizationEmployees
    };
});
